import { Person } from '@mui/icons-material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment/moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  SolaForceDataGrid,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportTypes,
  PersonnelListReportData,
  usePersonnelListReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import {
  convertToDateOrUndefined,
  getFormattedDateString,
} from '~/utils/helpers/date';
import { getEnumName } from '~/utils/helpers/enum';

export const PersonnelListReport = () => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const apiRef = useGridApiRef();

  const { getEmployeeById } = useAllEmployees();
  const { getEnums } = useAllEnums();
  const enums = useMemo(() => {
    return {
      positionEnums: getEnums(EnumListKey.EMPLOYEE_POSITION),
      countryEnums: getEnums(EnumListKey.COUNTRY),
    };
  }, [getEnums]);

  const [startDate, setStartDate] = useState<Date>(new Date());

  const { data, isLoading } = usePersonnelListReportData({
    reportType: {
      type: AnalyticsReportTypes.Company,
    },
    params: {
      startDate: startDate,
      endDate: startDate,
      empScope: AnalyticsReportEmployeeScope.AllEmployees,
    },
  });

  const dataColumnOptions = useMemo(() => {
    return data.reduce(
      (acc, row) => {
        const { fEmpWorkCountry, fEmpPosition } = row;

        const includeIfMissing = (options: string[], value: string) => {
          if (value && !options.includes(value)) {
            options.push(value);
          }
        };

        includeIfMissing(acc.countryOptions, fEmpWorkCountry);
        includeIfMissing(acc.positionOptions, fEmpPosition);

        return acc;
      },
      {
        countryOptions: [],
        positionOptions: [],
      } as {
        countryOptions: string[];
        positionOptions: string[];
      },
    );
  }, [data]);

  const getEnumValueOptions = (
    enumOptions: Enum[],
    filterOptionCodes?: string[],
  ) => {
    const availableEnums = filterOptionCodes
      ? enumOptions.filter(({ code }) => filterOptionCodes.includes(code))
      : enumOptions;

    return availableEnums.map((e) => e.name);
  };

  const columns = useMemo((): GridColDef<PersonnelListReportData>[] => {
    const { countryEnums, positionEnums } = enums;
    const { countryOptions, positionOptions } = dataColumnOptions;

    return [
      {
        field: 'fEmpId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'fEmpFirstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'fEmpLastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'empNum',
        headerName: t('laEmpId'),
        valueGetter: ({ row: { fEmpId } }) =>
          getEmployeeById(fEmpId)?.fEmpPersonNumber,
        minWidth: 130,
      },
      {
        field: 'fEmpUnit',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'fEmpUnitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 110,
      },
      {
        field: 'fEmpPosition',
        headerName: t('laPosition'),
        type: 'singleSelect',
        valueGetter: ({ value }) => getEnumName(positionEnums, value),
        valueOptions: getEnumValueOptions(positionEnums, positionOptions),
        minWidth: 150,
      },
      {
        field: 'fEmpStartDate',
        headerName: t('laStartDate'),
        type: 'date',
        valueGetter: ({ value }) => convertToDateOrUndefined(value),
        valueFormatter: ({ value }) => getFormattedDateString(value, 'L'),
        minWidth: 130,
      },
      {
        field: 'fEmpWorkCountry',
        headerName: t('laCountry'),
        type: 'singleSelect',
        valueGetter: ({ value }) => getEnumName(countryEnums, value),
        valueOptions: getEnumValueOptions(countryEnums, countryOptions),
        minWidth: 110,
      },
      {
        field: 'fEmpLegalEntityName',
        headerName: t('laLegalEntity'),
        minWidth: 210,
      },
      {
        field: 'fEmpCostCenterName',
        headerName: t('laCostCenter'),
        minWidth: 210,
      },
      {
        field: 'fEmpCostCenterCode',
        headerName: t('laCostCenterCode'),
        minWidth: 130,
      },
    ];
  }, [t, enums, dataColumnOptions, getEmployeeById, goToEmployeeBasicDataView]);

  return (
    <ReportViewLayout
      heading={t('laPersonelList')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <ReportDateRangeSelector
                startDate={startDate}
                endDate={startDate}
                onRangeChange={(newStartDate) => setStartDate(newStartDate!)}
                minDate={moment().startOf('year').subtract(9, 'years').toDate()}
                maxDate={moment().toDate()}
                mode={ReportDateRangeSelectorMode.SingleDateRange}
              />
              <ReportDatasetRowCount
                count={data.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility apiRef={apiRef} isLoading={isLoading} />
              <ReportDataExport
                title={t('laPersonelList')}
                startDate={startDate}
                apiRef={apiRef}
                isLoading={isLoading}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={data}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.fEmpId}
        pinnedColumns={{
          left: ['fEmpId', 'fEmpFirstName', 'fEmpLastName'],
        }}
      />
    </ReportViewLayout>
  );
};
